/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback, useRef } from 'react';
//import { useConversation } from '@11labs/react';
import { websocketVirtualAgent } from 'components/virtualagentws/websocketVirtualAgent';
import { useHVAConversation } from './HVAFineTuningPlaygroundContext';
import { FeedbackMessage } from './HVAFineTuningPlaygroundContext';
import { useAppContext } from 'providers/AppProvider';
import { Col, Row } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMicrophone,
  faMicrophoneSlash,
  faPhoneSlash,
  faPhoneVolume,
  faCircleNotch
} from '@fortawesome/free-solid-svg-icons';
import {
  conversationalEngineStartCall,
  conversationalEngineNotifyCallConnected,
  conversationalEngineEndCall,
  conversationalEngineToolRequest
} from 'services/hvaagent.service';
import {
  getVirtualAgentWsUrl,
  getAuthenticatedWsUrl
} from 'services/virtualagent.service';
import Avatar from 'components/base/Avatar';

interface ConversationMessage {
  source: 'user' | 'ai';
  message: string;
}

type HVAConversationWidgetProps = {
  agentId: string;
  agentImage?: string;
  campaignId: number;
  callType: 'Outbound' | 'Inbound';
  onError?: (error: Error) => void;
  onFeedback: (message: Omit<FeedbackMessage, 'id' | 'timestamp'>) => void;
  widgetHeight?: string;
};

const HVADynamicConversationWidget = ({
  agentId,
  agentImage = '',
  campaignId,
  callType,
  onError,
  onFeedback,
  widgetHeight = '550px'
}: HVAConversationWidgetProps): JSX.Element => {
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [clientToolsConfig, setClientToolsConfig] = useState({});
  // eslint-disable-next-line
  const [currentCallId, setCurrentCallId] = useState<string | null>(null);
  const callIdRef = useRef<string | null>(null);
  // Ref para rastrear si el componente está montado
  const isMountedRef = useRef(true);

  // eslint-disable-next-line
  const [agentAdapters, setAgentAdapters] = useState<{
    sttadapter: string;
    llmadapter: string;
    ttsadapter: string;
  } | null>(null);

  // Obtenemos el contexto de la conversación
  const {
    clientInfo,
    setIsNifValidated,
    setIsDobValidated,
    setRequestTransfer,
    setConversationResults
  } = useHVAConversation();

  // Obtenemos el contexto de la aplicación
  const {
    config: { isDark }
  } = useAppContext();

  // Referencia para el contenedor de mensajes
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Función para hacer scroll hasta el último mensaje
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const scrollContainer = messagesContainerRef.current;
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  };

  // Informamos a Conversational Engine de que la llamada está conectada
  const onConnect = useCallback(
    async (conversationInfo: { conversationId: string }) => {
      try {
        if (callIdRef.current) {
          await conversationalEngineNotifyCallConnected({
            agentId,
            callId: callIdRef.current,
            conversationid: conversationInfo.conversationId
          });

          if (isMountedRef.current) {
            onFeedback({
              type: 'info',
              message: 'Call connected',
              details: `Conversation ID: ${conversationInfo.conversationId}`,
              success: true
            });
          }
        }

        if (isMountedRef.current) {
          setIsInitialized(true);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to notify call connection:', error);

        if (isMountedRef.current) {
          setIsLoading(false);

          onFeedback({
            type: 'info',
            message: 'Error al conectar la llamada',
            details:
              error instanceof Error ? error.message : 'Error desconocido',
            success: false
          });
        }

        if (onError && isMountedRef.current) {
          onError(
            error instanceof Error ? error : new Error('Failed to connect call')
          );
        }
      }
    },
    [agentId, onFeedback, onError]
  );

  // Informamos a Conversational Engine de que la llamada está finalizada
  const onDisconnect = useCallback(async () => {
    try {
      // Solo ejecutamos end call si tenemos un callId
      if (callIdRef.current) {
        const response = await conversationalEngineEndCall({
          connid: callIdRef.current,
          telephonyresult: 'answered'
        });

        // Convertir response.data a string JSON y luego analizarlo de nuevo
        const responseString = JSON.stringify(response.data);
        const parsedData = JSON.parse(responseString);

        // Acceder a los datos
        let state = 'N/A';
        let telephonyresult = 'N/A';
        let businessresult = 'N/A';
        let rpc = 'N/A';
        let summary = '';
        let dataprovided = null;

        // Intentar extraer los datos del objeto parseado
        if (parsedData && parsedData.data) {
          state = parsedData.data.state || 'N/A';
          telephonyresult = parsedData.data.telephonyresult || 'N/A';
          businessresult = parsedData.data.businessresult || 'N/A';
          rpc =
            parsedData.data.rpc === true
              ? 'Sí'
              : parsedData.data.rpc === false
              ? 'No'
              : 'N/A';
          summary = parsedData.data.summary || '';
          dataprovided = parsedData.data.dataprovided || null;

          let combinedResults = '';

          // Añadir el resumen si existe
          if (summary) {
            combinedResults += `${summary}`;
          }

          // Añadir los datos capturados si existen
          if (dataprovided) {
            // Añadir separador si ya hay resumen
            if (combinedResults) {
              combinedResults += '\n\nCollected Data by the Agent\n';
            }

            combinedResults += `${dataprovided}`;
          }

          // Actualizar los resultados de la conversación con el contenido combinado
          if (isMountedRef.current) {
            setConversationResults(combinedResults);
          }
        }

        // Feedback al usuario con los valores extraídos
        if (isMountedRef.current) {
          onFeedback({
            type: 'close',
            message: 'Call ended',
            details: `Status: ${state}
        | PhoneResult: ${telephonyresult}
        | BusinessResult: ${businessresult}
        | RPC: ${rpc}`,
            success: true
          });
        }
      }

      if (isMountedRef.current) {
        setIsInitialized(false);
        setIsLoading(false);
        setCurrentCallId(null);
        setIsNifValidated(false);
        setIsDobValidated(false);
        setRequestTransfer(false);
      }

      // Limpiar la referencia (esto siempre es seguro)
      callIdRef.current = null;
    } catch (error) {
      console.error('Failed to end call:', error);

      // Feedback de error solo si está montado
      if (isMountedRef.current) {
        onFeedback({
          type: 'close',
          message: 'Error al finalizar la llamada',
          details: error instanceof Error ? error.message : 'Error desconocido',
          success: false
        });
      }

      if (onError && isMountedRef.current) {
        onError(
          error instanceof Error ? error : new Error('Failed to end call')
        );
      }
    }
  }, [
    onError,
    onFeedback,
    setIsNifValidated,
    setIsDobValidated,
    setRequestTransfer,
    setConversationResults
  ]);

  // Actualizar la configuración de herramientas del cliente cuando cambian las validaciones
  useEffect(() => {
    setClientToolsConfig({
      // --- Grupo requestInfo ---
      DATETIME: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'info',
            message: 'Requesting date and time',
            details: 'Processing request...',
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'DATETIME',
            parameters
          });

          onFeedback({
            type: 'info',
            message: 'Date and time requested',
            details: `Current time: ${
              result.data?.formatted || 'Not available'
            }`,
            success: result.success
          });

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'info',
            message: 'Error getting date and time',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      COMPANYNAME: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'info',
            message: 'Requesting company name',
            details: 'Processing request...',
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'COMPANYNAME',
            parameters
          });

          onFeedback({
            type: 'info',
            message: 'Company name requested',
            details: `Company: ${result.data?.companyName || 'Not available'}`,
            success: result.success
          });

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'info',
            message: 'Error getting company name',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      NAME: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'info',
            message: 'Requesting customer name',
            details: 'Processing request...',
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'NAME',
            parameters
          });

          onFeedback({
            type: 'info',
            message: 'Customer name requested',
            details: `Name: ${result.data?.name || 'Not available'}`,
            success: result.success
          });

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'info',
            message: 'Error getting customer name',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      PHONENUMBER: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'info',
            message: 'Requesting customer phone',
            details: 'Processing request...',
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'PHONENUMBER',
            parameters
          });

          onFeedback({
            type: 'info',
            message: 'Customer phone requested',
            details: `Phone: ${result.data?.phoneNumber || 'Not available'}`,
            success: result.success
          });

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'info',
            message: 'Error getting customer phone',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      OUTBOUNDCUSTOMERINFO: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'info',
            message: 'Requesting customer information',
            details: 'Processing request...',
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'OUTBOUNDCUSTOMERINFO',
            parameters
          });

          if (!result.success && result.error === 'PENDING_VALIDATIONS') {
            onFeedback({
              type: 'info',
              message: 'Customer information requested',
              details:
                'Required validations not completed: ' +
                result.pendingValidations?.join(', '),
              success: false
            });
          } else if (result.success) {
            onFeedback({
              type: 'info',
              message: 'Customer information retrieved',
              details: 'Customer information accessed successfully',
              success: true
            });
          } else {
            onFeedback({
              type: 'info',
              message: 'Error retrieving customer information',
              details: result.message || 'Unknown error',
              success: false
            });
          }

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'info',
            message: 'Error getting customer information',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      OUTBOUNDAPICUSTOMERINFO: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'info',
            message: 'Requesting customer information via API',
            details: 'Processing request...',
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'OUTBOUNDAPICUSTOMERINFO',
            parameters
          });

          if (!result.success && result.error === 'PENDING_VALIDATIONS') {
            onFeedback({
              type: 'info',
              message: 'Customer information requested via API',
              details:
                'Required validations not completed: ' +
                result.pendingValidations?.join(', '),
              success: false
            });
          } else if (result.success) {
            onFeedback({
              type: 'info',
              message: 'Customer information retrieved via API',
              details: 'Customer information accessed successfully',
              success: true
            });
          } else {
            onFeedback({
              type: 'info',
              message: 'Error retrieving customer information via API',
              details: result.message || 'Error desconocido',
              success: false
            });
          }

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'info',
            message: 'Error getting customer information via API',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      OUTBOUNDCOMPANYINFO: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'info',
            message: 'Requesting company information',
            details: 'Processing request...',
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'OUTBOUNDCOMPANYINFO',
            parameters
          });

          if (!result.success && result.error === 'PENDING_VALIDATIONS') {
            onFeedback({
              type: 'info',
              message: 'Company information requested',
              details:
                'Required validations not completed: ' +
                result.pendingValidations?.join(', '),
              success: false
            });
          } else if (result.success) {
            onFeedback({
              type: 'info',
              message: 'Company information retrieved',
              details: 'Company information accessed successfully',
              success: true
            });
          } else {
            onFeedback({
              type: 'info',
              message: 'Error retrieving company information',
              details: result.message || 'Error desconocido',
              success: false
            });
          }

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'info',
            message: 'Error retrieving company information',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      INBOUNDCUSTOMERINFO: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'info',
            message: 'Requesting inbound customer information',
            details: 'Processing request...',
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'INBOUNDCUSTOMERINFO',
            parameters
          });

          onFeedback({
            type: 'info',
            message: 'Inbound customer information',
            details: result.success
              ? 'Information retrieved successfully'
              : result.message || 'Unknown error',
            success: result.success
          });

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'info',
            message: 'Error getting inbound customer information',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      CATALOG: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'info',
            message: 'Requesting catalog information',
            details: 'Processing request...',
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'CATALOG',
            parameters
          });

          onFeedback({
            type: 'info',
            message: 'Catalog information',
            details: result.success
              ? 'Catalog retrieved successfully'
              : result.message || 'Unknown error',
            success: result.success
          });

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'info',
            message: 'Error getting catalog information',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      ORDER: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'info',
            message: 'Requesting order information',
            details: 'Processing request...',
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'ORDER',
            parameters
          });

          onFeedback({
            type: 'info',
            message: 'Order information',
            details: result.success
              ? 'Order retrieved successfully'
              : result.message || 'Unknown error',
            success: result.success
          });

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'info',
            message: 'Error getting order information',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      KNOWLEDGESEARCH: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'info',
            message: 'Searching knowledge base',
            details: `Querying: "${parameters.query || 'No query'}"`,
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'KNOWLEDGESEARCH',
            parameters
          });

          if (result.success) {
            const resultsCount = result.data?.results?.length || 0;
            const totalFound = result.data?.total_found || 0;

            onFeedback({
              type: 'info',
              message: 'Search completed',
              details: `Found ${totalFound} relevant results${
                resultsCount > 0
                  ? `. Showing the ${resultsCount} most relevant.`
                  : ', but none exceed the relevance threshold.'
              }`,
              success: true
            });

            // Si hay resultados, mostrar un resumen
            if (resultsCount > 0) {
              const topResult = result.data.results[0];
              onFeedback({
                type: 'info',
                message: 'Most relevant result',
                details: `Source: ${
                  topResult.source || 'Document'
                } | Relevance: ${Math.round(
                  (topResult.relevance || 0) * 100
                )}%`,
                success: true
              });
            }
          } else {
            onFeedback({
              type: 'info',
              message: 'Error searching knowledge base',
              details: result.message || result.error,
              success: false
            });
          }

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'info',
            message: 'Knowledge base error',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      WEBSEARCH: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'info',
            message: 'Searching the internet',
            details: `Querying Google: "${parameters.query || 'No query'}"`,
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'WEBSEARCH',
            parameters
          });

          if (result.success) {
            const resultsCount = result.data?.results?.length || 0;
            const totalFound = result.data?.total_found || 0;

            onFeedback({
              type: 'info',
              message: 'Search completed',
              details: `Found ${totalFound} relevant results${
                resultsCount > 0
                  ? `. Showing the ${resultsCount} most relevant.`
                  : ', but none exceed the relevance threshold.'
              }`,
              success: true
            });

            // Si hay resultados, mostrar un resumen
            if (resultsCount > 0) {
              const topResult = result.data.results[0];
              onFeedback({
                type: 'info',
                message: 'Most relevant web result',
                details: `Website: ${
                  topResult.source || 'Unknown'
                } | Relevance: ...`,
                success: true
              });
            }
          } else {
            onFeedback({
              type: 'info',
              message: 'Web search error',
              details: result.message || result.error,
              success: false
            });
          }

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'info',
            message: 'Error searching the web',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      CUSTOMERHISTORY: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          // Determinar el período de búsqueda
          const lookbackDays = parameters?.lookbackDays || 30;

          onFeedback({
            type: 'info',
            message: 'Requesting customer history',
            details: `Looking back ${lookbackDays} days...`,
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'CUSTOMERHISTORY',
            parameters
          });

          if (result.success) {
            const data = result.data || {};
            const totalCalls = data.totalCalls || 0;
            const answeredCalls = data.answeredCalls || 0;
            const relevantCalls = data.relevantCalls || 0;

            onFeedback({
              type: 'info',
              message: 'Customer history retrieved',
              details: `Total: ${totalCalls} calls | Answered: ${answeredCalls} | Relevant: ${relevantCalls} (>15s)`,
              success: true
            });
          } else {
            onFeedback({
              type: 'info',
              message: 'Error retrieving customer history',
              details: result.message || 'Unknown error',
              success: false
            });
          }

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'info',
            message: 'Error getting customer history',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      // --- Grupo requestValidation ---
      CUSTOMERIDNUMBER: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'validation',
            message: 'Performing customer ID validation',
            details: `Validating ID: ${
              parameters.valueprovided || 'Not provided'
            }`,
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'CUSTOMERIDNUMBER',
            parameters
          });

          if (!result.success) {
            onFeedback({
              type: 'validation',
              message: 'ID validation requested',
              details: result.message || 'Error validating customer ID',
              success: false
            });
            setIsNifValidated(false);
          } else {
            onFeedback({
              type: 'validation',
              message: 'ID validation completed',
              details: `Result: ${
                result.data?.isValid ? '✓ ID matches' : '✗ ID does not match'
              }`,
              success: result.data?.isValid
            });
            setIsNifValidated(result.data?.isValid || false);
          }

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'validation',
            message: 'Error in ID validation',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'VALIDATION_ERROR',
            message: errorMessage
          });
        }
      },
      PARTIALCUSTOMERIDNUMBER: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'validation',
            message: 'Performing partial ID validation',
            details: `Validating partial ID: ${
              parameters.valueprovided || 'Not provided'
            }`,
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'PARTIALCUSTOMERIDNUMBER',
            parameters
          });

          if (!result.success) {
            onFeedback({
              type: 'validation',
              message: 'Partial ID validation requested',
              details: result.message || 'Error validating partial ID',
              success: false
            });
            setIsNifValidated(false);
          } else {
            onFeedback({
              type: 'validation',
              message: 'Partial ID validation completed',
              details: `Result: ${
                result.data?.isValid
                  ? '✓ Partial ID matches'
                  : '✗ Partial ID does not match'
              }`,
              success: result.data?.isValid
            });
            setIsNifValidated(result.data?.isValid || false);
          }

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'validation',
            message: 'Error in partial ID validation',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'VALIDATION_ERROR',
            message: errorMessage
          });
        }
      },
      DATEOFBIRTH: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'validation',
            message: 'Performing date of birth validation',
            details: `Validating date: ${
              parameters.valueprovided || 'Not provided'
            }`,
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'DATEOFBIRTH',
            parameters
          });

          if (!result.success) {
            onFeedback({
              type: 'validation',
              message: 'Date of birth validation requested',
              details: result.message || 'Error validating date of birth',
              success: false
            });
            setIsDobValidated(false);
          } else {
            onFeedback({
              type: 'validation',
              message: 'Date of birth validation completed',
              details: `Result: ${
                result.data?.isValid
                  ? '✓ Date matches'
                  : '✗ Date does not match'
              }`,
              success: result.data?.isValid
            });
            setIsDobValidated(result.data?.isValid || false);
          }

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'validation',
            message: 'Error in date of birth validation',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'VALIDATION_ERROR',
            message: errorMessage
          });
        }
      },
      CUSTOMERREFERENCE: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'validation',
            message: 'Performing customer reference validation',
            details: `Validating reference: ${
              parameters.valueprovided || 'Not provided'
            }`,
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'CUSTOMERREFERENCE',
            parameters
          });

          onFeedback({
            type: 'validation',
            message: 'Customer reference validation completed',
            details: `Result: ${
              result.data?.isValid
                ? '✓ Reference matches'
                : '✗ Reference does not match'
            }`,
            success: result.data?.isValid
          });

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'validation',
            message: 'Error in customer reference validation',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'VALIDATION_ERROR',
            message: errorMessage
          });
        }
      },
      ADDRESS: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'validation',
            message: 'Performing address validation',
            details: `Validating address: ${
              parameters.valueprovided || 'Not provided'
            }`,
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'ADDRESS',
            parameters
          });

          onFeedback({
            type: 'validation',
            message: 'Address validation completed',
            details: `Result: ${
              result.data?.isValid
                ? '✓ Address matches'
                : '✗ Address does not match'
            }`,
            success: result.data?.isValid
          });

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'validation',
            message: 'Error in address validation',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'VALIDATION_ERROR',
            message: errorMessage
          });
        }
      },
      EMAIL: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'validation',
            message: 'Performing email validation',
            details: `Validating email: ${
              parameters.valueprovided || 'Not provided'
            }`,
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'EMAIL',
            parameters
          });

          onFeedback({
            type: 'validation',
            message: 'Email validation completed',
            details: `Result: ${
              result.data?.isValid
                ? '✓ Email matches'
                : '✗ Email does not match'
            }`,
            success: result.data?.isValid
          });

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'validation',
            message: 'Error in email validation',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'VALIDATION_ERROR',
            message: errorMessage
          });
        }
      },
      DATEOFBIRTHANDFATHERNAME: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          // Validar que tenemos los tres parámetros requeridos
          if (
            !parameters.dateOfBirth ||
            !parameters.fatherName ||
            !parameters.fatherNameLatin
          ) {
            throw new Error(
              'Missing required parameters: dateOfBirth, fatherName, and fatherNameLatin are required'
            );
          }

          onFeedback({
            type: 'validation',
            message: "Performing date of birth and father's name validation",
            details: `Validating DOB: ${parameters.dateOfBirth}, Father: ${parameters.fatherName} / ${parameters.fatherNameLatin}`,
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'DATEOFBIRTHANDFATHERNAME',
            parameters: {
              dateOfBirth: parameters.dateOfBirth,
              fatherName: parameters.fatherName,
              fatherNameLatin: parameters.fatherNameLatin
            }
          });

          // Manejar la respuesta con mensajes específicos
          if (!result.success) {
            let details = result.message || 'Validation failed';

            // Mensajes específicos según el error
            if (result.error === 'FATHERNAME_NOT_AVAILABLE') {
              details =
                "Father's name validation not available for this customer";
            } else if (result.error === 'DOB_NOT_FOUND') {
              details = 'Date of birth not found in customer data';
            } else if (result.error === 'INVALID_DATE_FORMAT') {
              details = 'Invalid date format. Use YYYYMMDD';
            }

            onFeedback({
              type: 'validation',
              message: 'Identity validation failed',
              details,
              success: false
            });
          } else {
            // Mostrar resultados detallados
            const details = result.data?.details;
            let feedbackMessage = '';

            if (result.data?.isValid) {
              feedbackMessage = 'Identity verified successfully';
            } else {
              if (details?.dateOfBirthMatch && !details?.fatherNameMatch) {
                feedbackMessage = `DOB matches | Father's name doesn't match (${details?.fatherNameSimilarity}% similarity)`;
              } else if (
                !details?.dateOfBirthMatch &&
                details?.fatherNameMatch
              ) {
                feedbackMessage = `DOB doesn't match | Father's name matches (${details?.fatherNameSimilarity}% similarity)`;
              } else {
                feedbackMessage = `Neither DOB nor father's name match`;
              }
            }

            onFeedback({
              type: 'validation',
              message: result.data?.isValid
                ? 'Identity validation successful'
                : 'Identity validation failed',
              details: feedbackMessage,
              success: result.data?.isValid
            });
          }

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'validation',
            message: 'Error in identity validation',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'VALIDATION_ERROR',
            message: errorMessage
          });
        }
      },
      // --- Grupo requestAction ---
      TRANSFERTOSUPERVISOR: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'transfer',
            message: 'Transfer to supervisor requested',
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'TRANSFERTOSUPERVISOR',
            parameters
          });

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'transfer',
            message: 'Error requesting transfer to supervisor',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      TRANSFERTOPOS: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          onFeedback({
            type: 'transfer',
            message: 'Transfer to POS requested',
            success: true
          });

          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'TRANSFERTOPOS',
            parameters
          });

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onFeedback({
            type: 'transfer',
            message: 'Error requesting transfer to POS',
            details: errorMessage,
            success: false
          });
          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      },
      // Añadir después de TRANSFERTOPOS, alrededor de la línea 1050
      SENDSMS: async (parameters: any) => {
        try {
          if (!callIdRef.current) {
            throw new Error('No active call found');
          }

          // Feedback inicial
          onFeedback({
            type: 'info',
            message: 'Preparing SMS send',
            details: `Template: ${parameters.templateId || 'Not specified'}`,
            success: true
          });

          // Llamar al servicio
          const result = await conversationalEngineToolRequest({
            callId: callIdRef.current,
            tool: 'SENDSMS',
            parameters
          });

          // Procesar respuesta
          if (result.success) {
            // Extraer información del resultado
            const smsData = result.data || {};
            const phoneNumber =
              smsData.to || parameters.phoneNumber || 'Número del cliente';
            const templateUsed = smsData.template || parameters.templateId;
            const remainingSMS =
              smsData.remainingSMS !== undefined ? smsData.remainingSMS : 'N/A';

            onFeedback({
              type: 'info',
              message: 'SMS sent successfully',
              details: `Sent to: ${phoneNumber} | Template: ${templateUsed} | Remaining SMS: ${remainingSMS}`,
              success: true
            });

            // Si hay un ID de mensaje de ClickSend, mostrarlo
            if (smsData.messageId) {
              onFeedback({
                type: 'info',
                message: 'ClickSend message ID',
                details: smsData.messageId,
                success: true
              });
            }
          } else {
            onFeedback({
              type: 'info',
              message: 'Error sending SMS',
              details: result.message || result.error,
              success: false
            });

            // Si hay información adicional del SMS fallido, mostrarla
            if (result.data?.smsId) {
              onFeedback({
                type: 'info',
                message: 'SMS record ID',
                details: `ID: ${result.data.smsId} (Status: FAILED)`,
                success: false
              });
            }
          }

          return JSON.stringify(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';

          onFeedback({
            type: 'info',
            message: 'Critical error sending SMS',
            details: errorMessage,
            success: false
          });

          return JSON.stringify({
            success: false,
            error: 'EXECUTION_ERROR',
            message: errorMessage
          });
        }
      }
    });
  }, []);

  const conversation = websocketVirtualAgent({
    onConnect: async conversationInfo => {
      console.log('Connected to agent:', conversationInfo.conversationId);
      try {
        await onConnect(conversationInfo);
      } catch (error) {
        console.error('Connection error:', error);
      }
    },
    onDisconnect: async () => {
      console.log('Disconnected from agent');
      try {
        await onDisconnect();
      } catch (error) {
        console.error('Disconnection error:', error);
      }
    },
    onError: (message, context) => {
      console.error('Conversation error:', message, context);
      setIsLoading(false);

      // Feedback de error
      onFeedback({
        type: 'info',
        message: 'Error en la conversación',
        details: message || 'Error desconocido',
        success: false
      });

      if (onError) {
        onError(new Error(message));
      }
    },
    onMessage: message => {
      setMessages(prev => [
        ...prev,
        {
          source: message.source,
          message: message.message
        }
      ]);

      setTimeout(scrollToBottom, 0);
    },
    // Añadimos la configuración de client tools
    clientTools: clientToolsConfig
  });

  const requestMicPermission = async (): Promise<boolean> => {
    try {
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      setHasPermission(true);
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      setHasPermission(false);

      // Feedback de error
      onFeedback({
        type: 'info',
        message: 'Permiso de micrófono denegado',
        details: 'Se requiere acceso al micrófono para la conversación',
        success: false
      });

      if (onError) {
        onError(new Error('Microphone permission denied'));
      }
      return false;
    }
  };

  const startConversation = useCallback(async (): Promise<void> => {
    if (!hasPermission) {
      const permitted = await requestMicPermission();
      if (!permitted) return;
    }

    try {
      // ⚠️ PROTECCIÓN: Solo continuar si el componente está montado
      if (!isMountedRef.current) return;

      setIsLoading(true);
      setIsNifValidated(false);
      setIsDobValidated(false);
      setRequestTransfer(false);
      setMessages([]);

      // Limpiar sesión anterior si existe
      await conversation.endSession();

      // Validar que tenemos información de cliente necesaria
      if (!clientInfo) {
        throw new Error('No client information available for the call');
      }

      // Usar el teléfono del cliente si está disponible, si no, usar uno por defecto
      const testPhoneNumber = clientInfo.customerphone || '+34629118463';

      // Preparar datos de cliente para el API
      const callData = {
        bxpCampaignID: campaignId,
        agentId: agentId,
        phonenumber: testPhoneNumber,
        calltype: callType,

        // Campos básicos directamente del clientInfo
        companyname: clientInfo.companyname,
        customername: clientInfo.customername,
        customertrn: clientInfo.customertrn,
        customerreference: clientInfo.customerreference,
        customerdob: clientInfo.customerdob
          ? clientInfo.customerdob.toISOString().split('T')[0]
          : undefined,
        customerfathername: clientInfo.customerfathername,
        customeraddress: clientInfo.customeraddress,
        customeremail: clientInfo.customeremail,
        customeriscompany: clientInfo.customeriscompany || 0,
        importid: clientInfo.importid,
        recordid: clientInfo.recordid,

        // Contextos genéricos que vienen del clientInfo
        callcontext: clientInfo.callcontext || {},
        bxpcontext: clientInfo.bxpcontext || {}
      };

      // ⚠️ PROTECCIÓN: Verificar antes de feedback
      if (!isMountedRef.current) return;

      // Feedback - iniciando llamada
      onFeedback({
        type: 'info',
        message: `Starting ${callType} call`,
        details: `Customer: ${callData.customername}, Phone: ${testPhoneNumber}, Campaign: ${callData.bxpCampaignID}, ImportID: ${callData.importid}, RecordID: ${callData.recordid}`,
        success: true
      });

      // Iniciar llamada en Conversational Engine
      const callResponse = await conversationalEngineStartCall(callData);

      // ⚠️ PROTECCIÓN: Verificar antes de actualizar refs y estados
      if (!isMountedRef.current) return;

      // Guardamos el callId en el estado y ref para futuras operaciones
      callIdRef.current = callResponse.callId;
      //const localAgentId = callResponse.agentid || agentId;
      setCurrentCallId(callResponse.callId);

      // Guardar los adaptadores si vienen en la respuesta
      if (callResponse.agentaiadapters) {
        setAgentAdapters(callResponse.agentaiadapters);

        // Feedback sobre los adaptadores
        onFeedback({
          type: 'info',
          message: 'IA adapters',
          details: `STT: ${callResponse.agentaiadapters.sttadapter}, LLM: ${callResponse.agentaiadapters.llmadapter}, TTS: ${callResponse.agentaiadapters.ttsadapter}`,
          success: true
        });
      }

      // Feedback - llamada iniciada
      onFeedback({
        type: 'info',
        message: 'Call started',
        details: `Call ID: ${callResponse.callId} | Type: ${callType}`,
        success: true
      });

      // Obtener URL backend
      let virtualAgentUrl;
      if (import.meta.env.VITE_NODE_ENV === 'production') {
        // En producción, usar la función asíncrona
        virtualAgentUrl = await getAuthenticatedWsUrl();
      } else {
        // En desarrollo, usar la URL directa
        virtualAgentUrl = getVirtualAgentWsUrl();
      }

      // ⚠️ PROTECCIÓN: Verificar antes de iniciar sesión
      if (!isMountedRef.current) return;

      // Iniciar sesión con URL firmada
      await conversation.startSession({
        signedUrl: virtualAgentUrl,
        overrides: {
          agent: {
            prompt: {
              prompt: callResponse.prompt
            },
            firstMessage: callResponse.firstMessage,
            language: callResponse.language,
            tools: callResponse.tools,
            agentaiadapters: callResponse.agentaiadapters || {
              sttadapter: '',
              llmadapter: '',
              ttsadapter: ''
            }
          },
          tts: {
            voiceId: callResponse.voice
          }
        }
      });
    } catch (error) {
      console.error('Failed to start conversation:', error);

      // ⚠️ PROTECCIÓN: Solo actualizar si está montado
      if (isMountedRef.current) {
        setIsLoading(false);

        // Feedback de error
        onFeedback({
          type: 'info',
          message: 'Error starting conversation',
          details: error instanceof Error ? error.message : 'Error unknown',
          success: false
        });
      }

      if (error instanceof Error && onError && isMountedRef.current) {
        onError(error);
      }
    }
  }, [
    agentId,
    campaignId,
    conversation,
    hasPermission,
    clientInfo,
    onError,
    onFeedback,
    setIsNifValidated,
    setIsDobValidated,
    setRequestTransfer,
    callType
  ]);

  const endConversation = useCallback(async (): Promise<void> => {
    try {
      // Solo actualizar si está montado
      if (!isMountedRef.current) return;

      setIsLoading(true);

      // Feedback - finalizando llamada
      onFeedback({
        type: 'info',
        message: 'Ending call',
        details: callIdRef.current
          ? `ID de llamada: ${callIdRef.current}`
          : 'Sin ID de llamada',
        success: true
      });

      await conversation.endSession();
    } catch (error) {
      console.error('Failed to end conversation:', error);

      // ⚠️ PROTECCIÓN: Feedback solo si está montado
      if (isMountedRef.current) {
        onFeedback({
          type: 'info',
          message: 'Error al finalizar conversación',
          details: error instanceof Error ? error.message : 'Error desconocido',
          success: false
        });
      }

      if (error instanceof Error && onError) {
        onError(error);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
        setAgentAdapters(null);
      }
    }
  }, [conversation, onError, onFeedback]);

  // Efecto para inicializar el scroll cuando se montan los mensajes
  useEffect(() => {
    if (isMountedRef.current && messagesContainerRef.current) {
      scrollToBottom();
    }
  }, [messages]);

  // Efecto para controlar el montaje del componente
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Efecto para acceder al micrófono al cargar el componente
  useEffect(() => {
    void requestMicPermission();
    return () => {
      if (isInitialized) {
        void endConversation();
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center p-0 pb-3">
      <Row className="mb-1 mt-2">
        <Col
          className="pe-0"
          style={{ maxWidth: '230px', minWidth: '230px', width: '230px' }}
        >
          <div className="d-flex bg-primary rounded-5 w-100 justify-content-start border-1 p-1">
            <Avatar
              size="l"
              src={agentImage}
              alt="Agent Avatar"
              placeholder={!agentImage}
              variant="image"
              rounded="circle"
              imageClassName="avatar-image-top bg-body"
            />
            {!isInitialized ? (
              <Button
                onClick={() => void startConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
                disabled={!hasPermission || isLoading}
              >
                {isLoading ? (
                  <>
                    <FontAwesomeIcon
                      icon={faCircleNotch}
                      className="me-2 fs-8"
                      spin
                    />
                    Connecting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon
                      icon={faPhoneVolume}
                      className="me-2 fs-8"
                    />
                    Start call
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={() => void endConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <FontAwesomeIcon
                      icon={faCircleNotch}
                      className="me-2 fs-8"
                      spin
                    />
                    Ending...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon
                      icon={faPhoneSlash}
                      className="me-2 fs-8"
                    />
                    End call
                  </>
                )}
              </Button>
            )}
          </div>
        </Col>
        <Col className="d-flex justify-content-end">
          <div className="d-flex justify-content-end text-end fs-10 me-2">
            <Row className="text-body-secondary">
              <div className="pt-1">
                Status:
                {conversation.status === 'connected' ? (
                  <FontAwesomeIcon
                    icon={faPhoneVolume}
                    className="mx-2 me-0 fs-10 text-success"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faPhoneSlash}
                    className="mx-2 me-0 fs-10 text-danger"
                  />
                )}
              </div>
              <div>
                {conversation.status === 'connected' && (
                  <>
                    Micrófono:
                    {conversation.isSpeaking ? (
                      <FontAwesomeIcon
                        icon={faMicrophone}
                        className="mx-2 me-1 fs-10 text-success"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faMicrophoneSlash}
                        className="mx-1 me-0 fs-10 text-danger"
                      />
                    )}
                  </>
                )}
              </div>
            </Row>
          </div>
        </Col>
      </Row>

      <div
        ref={messagesContainerRef}
        className="p-4 border rounded scrollbar bg-body fs-9 lh-sm"
        style={{
          height: `${widgetHeight}`,
          overflowY: 'auto',
          scrollBehavior: 'smooth'
        }}
        translate="no"
      >
        {messages.length > 0 ? (
          <div className="w-full space-y-2">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`p-2 rounded mb-3 ${
                  msg.source === 'user'
                    ? isDark
                      ? 'bg-secondary'
                      : 'bg-secondary-lighter'
                    : 'bg-body-emphasis'
                }`}
              >
                <strong>{msg.source === 'user' ? 'Customer' : 'Agent'}:</strong>
                &nbsp;
                {msg.message}
              </div>
            ))}
          </div>
        ) : (
          isInitialized &&
          !isLoading && (
            <div className="text-center text-body-secondary py-5">
              The conversation will begin shortly...
            </div>
          )
        )}

        {isLoading && (
          <div className="text-center text-body-secondary py-5">
            <FontAwesomeIcon icon={faCircleNotch} className="me-2" spin />
            {isInitialized ? 'Proccesing...' : 'Starting conversation...'}
          </div>
        )}
      </div>

      {!hasPermission && (
        <div className="fs-9 p-2 bg-danger-subtle text-danger rounded-lg mt-2 text-center">
          <strong>Microphone permission is required</strong> for the
          conversation
        </div>
      )}
    </div>
  );
};

export default HVADynamicConversationWidget;
